import { useHistory } from 'react-router-dom'
import Protected from '../Protected'
import React, { useCallback, useRef, useState, useEffect } from 'react'
import { PATHS } from '../Routes'
import useErrorHandler from '../../hooks/useErrorHandler'
import FormErrorDisplay from '../common/FormErrorMessages'
import * as Yup from 'yup'
import { AbnSearchRow } from '../../models/AbrMessage'
import '../../assets/css/fwobusinessabnselector.css'
import { Formik, FormikHelpers } from 'formik'
import { Button, Form } from 'semantic-ui-react'
import FWOTextInput from '../form/FWOTextInput'
import FWOTable from '../helpers/FWOTable'
import { ChangeABNSearchFormValues } from '../../models/ChangeABN'
import AbnSearchService from '../../services/AbnSearchService'
import ProfileService from '../../services/ProfileService'
import EnquiryService from '../../services/EnquiryService'
import {
  UpdateUserAbnRequest,
  UpdateEnquiryAbnRequest,
  NewABN,
} from '../../models/ChnageABN'
import { validateABN } from '../../utilities/commonFunctions'
import Tooltip from '../common/Tooltip'

const ChangeABN = () => {
  const mountedRef = useRef(false)
  const [userErrors, , , ,] = useErrorHandler()
  const queryParams = new URLSearchParams(location.search)
  const formRef = useRef(null)
  const history = useHistory()
  const [submitEnable, setSubmitEnable] = useState(false)
  const [abnToChange, setAbnToChange] = useState<NewABN | null>(null)
  const validateSchema = Yup.object({
    searchTerm: Yup.string().required('Business name or ABN is required'),
  })
  const [searchData, setSearchData] = useState<AbnSearchRow[] | null>(null)
  const [requestOrigin, setRequestOrigin] = useState<string | null>(null)
  const initialValues = {
    searchTerm: '',
  }

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      if (
        queryParams.has('from') &&
        queryParams.get('from') !== null &&
        queryParams.get('from') === 'new-enquiry'
      ) {
        setRequestOrigin('new-enquiry')
      } else if (
        queryParams.has('from') &&
        queryParams.get('from') !== null &&
        queryParams.get('from') === 'edit-enquiry'
      ) {
        if (queryParams.has('id') && queryParams.get('id') !== null) {
          setRequestOrigin('edit-enquiry')
        }
      } else {
        setRequestOrigin('profile')
      }
    }
    return () => {
      mountedRef.current = false
    }
  }, [])

  const buildAbnSearchItems = (data): AbnSearchRow[] => {
    const result = data.map(
      (item: {
        abn: string
        name: string
        nameType: string
        state: string
        postcode: string
      }) => ({
        abn: item.abn,
        name: item.name,
        nameType: item.nameType,
        state: item.state,
        postcode: item.postcode,
      })
    )

    return result
  }

  const buildAbnLookupItem = (data): AbnSearchRow[] => {
    const abnLookupItems: AbnSearchRow[] = [
      {
        abn: data.abn,
        name: data.entityName,
        nameType: data.entityTypeCode,
        state: data.addressState,
        postcode: data.addressPostcode,
      },
    ]

    return abnLookupItems
  }
  const columns = React.useMemo(
    () => [
      {
        Header: 'Australian Business Register Information',
        hideHeader: true,
        columns: [
          {
            Header: 'ABN',
            accessor: 'abn',
          },
          {
            Header: 'Name',
            accessor: 'name',
          },
          {
            Header: 'Name Type',
            accessor: 'nameType',
          },
          {
            Header: 'State',
            accessor: 'state',
          },
          {
            Header: 'Post Code',
            accessor: 'postcode',
          },
        ],
      },
    ],
    []
  )
  const rowClicked = (row) => {
    setSubmitEnable(true)
    const newABNChange = new NewABN()
    newABNChange.abn = row.values.abn
    newABNChange.businessName = row.values.name
    newABNChange.crn = queryParams.get('crn')

    if (requestOrigin && requestOrigin === 'edit-enquiry') {
      newABNChange.enquiryId = queryParams.get('id')
    }

    setAbnToChange(newABNChange)
  }

  const submitAbnChange = async () => {
    if (!requestOrigin || !abnToChange) return

    if (requestOrigin === 'new-enquiry' || requestOrigin === 'profile') {
      const updateUserAbnRequest: UpdateUserAbnRequest = {
        abn: abnToChange.abn,
        businessName: abnToChange.businessName,
        crn: abnToChange.crn,
      }

      ProfileService.changeABN(updateUserAbnRequest).then((response) => {
        if (response) {
          if (requestOrigin === 'new-enquiry') {
            history.push('/my-account/enquiry/submit-a-new-enquiry')
          } else {
            history.push(PATHS.Profile)
          }
        }
        setSearchData(null)
      })
    } else {
      const updateEnquiryAbnRequest: UpdateEnquiryAbnRequest = {
        abn: abnToChange.abn,
        businessName: abnToChange.businessName,
        enquiryId: abnToChange.enquiryId,
      }

      EnquiryService.changeABN(updateEnquiryAbnRequest).then((response) => {
        if (response) {
          history.push(
            '/my-account/enquiry/' + queryParams.get('id') + '/edit/1'
          )
        }
        setSearchData(null)
      })
    }
  }

  const submitForm = async (values: ChangeABNSearchFormValues) => {
    setSearchData(null)
    if (validateABN(values.searchTerm) && values.searchTerm.trim().length > 0) {
      return await AbnSearchService.getAbnDetails(values.searchTerm)
    } else {
      return await AbnSearchService.getMatchingNames(values.searchTerm)
    }
  }

  const handleUserSearch = useCallback(
    (
      formFields: ChangeABNSearchFormValues,
      actions: FormikHelpers<ChangeABNSearchFormValues>
    ) => {
      return submitForm(formFields)
        .then((response) => {
          if (
            !validateABN(formFields.searchTerm) &&
            response.result.names.length > 0
          ) {
            const searchRows = buildAbnSearchItems(response.result.names)
            setSearchData(searchRows)
          } else {
            setSearchData(null)
          }
          if (
            validateABN(formFields.searchTerm) &&
            response &&
            response.result
          ) {
            const searchRows = buildAbnLookupItem(response.result)
            setSearchData(searchRows)
          }
        })
        .catch((error) => {
          console.log(error)
          window.scrollTo(0, 0)
        })
        .finally(() => {
          actions.setSubmitting(false)
        })
    },
    []
  )

  const cancelABNChange = () => {
    if (!requestOrigin) return

    if (requestOrigin === 'new-enquiry') {
      history.push('/my-account/enquiry/submit-a-new-enquiry')
    } else if (requestOrigin === 'edit-enquiry') {
      history.push('/my-account/enquiry/' + queryParams.get('id') + '/edit/1')
    } else {
      history.push(PATHS.Profile)
    }
  }

  const onKeyPressHandler = (event) => {
    console.log(event.keyCode)
    if (event.keyCode === 13) {
      if (!requestOrigin) return
      if (requestOrigin === 'new-enquiry') {
        history.push('/my-account/enquiry/submit-a-new-enquiry')
      } else if (requestOrigin === 'edit-enquiry') {
        history.push('/my-account/enquiry/' + queryParams.get('id') + '/edit/1')
      } else {
        history.push(PATHS.Profile)
      }
    }
  }

  return (
    <Protected>
      <nav className="breadcrumbs dnd-s-mob">
        <ol>
          <li>
            <a
              className="underline"
              onClick={cancelABNChange}
              role="button"
              onKeyDown={(e) => onKeyPressHandler(e)}
              tabIndex={0}
            >
              Back to previous page
            </a>
          </li>
        </ol>
      </nav>
      <div className="content-wrapper">
        <article className="main">
          <div className="table-wrapper">
            <h1>Change business name (ABN)</h1>
            <p>
              Sometimes called the legal or employing entity. For companies it
              often ends in Pty Ltd. If the employer is a sole trader, it&apos;s
              often the employer&apos;s name eg. Fred Jones. The business name
              can be different to the trading name Eg. a business name XYZ Pty
              Ltd may have the trading name, Jake&apos;s plumbing services.
            </p>
            <p>
              Searching using the ABN is recommended. The ABN can usually be
              found on a pay slip.
            </p>
            <p>
              Fill in the business name or ABN below, click on
              &apos;Search&apos; and then &apos;Select&apos; the correct search
              result, then click on &apos;Submit&apos;.
            </p>
            <p>
              After clicking &apos;submit&apos;, this page will close and you
              will be returned to the previous form so that you can complete it.
            </p>
            <FormErrorDisplay
              show={userErrors?.length > 0}
              errorsList={userErrors}
              heading="Please review the following errors"
            />
            <Formik
              innerRef={formRef}
              validationSchema={validateSchema}
              enableReinitialize
              initialValues={initialValues}
              onSubmit={(values, actions) => {
                actions.setSubmitting(true)
                handleUserSearch(values, actions)
              }}
            >
              {({ handleSubmit, isSubmitting }) => (
                <Form
                  className="ui form osp-variant large"
                  autoComplete="off"
                  onSubmit={handleSubmit}
                >
                  <div className="sixteen wide field">
                    <p>
                      <b> Enter business name (ABN)</b>
                      <Tooltip>
                        Sometimes called the legal or employing entity. For
                        companies it often ends in Pty Ltd. If the employer is a
                        sole trader, it&apos;s often the employer&apos;s name
                        eg. Fred Jones. The business name can be different to
                        the trading name Eg. a business name XYZ Pty Ltd may
                        have the trading name, Jake&apos;s plumbing services.
                      </Tooltip>
                    </p>

                    <FWOTextInput
                      type="text"
                      placeholder="Enter business name or ABN"
                      label=""
                      ariaLabel="Enter business name or ABN"
                      name="searchTerm"
                      required={true}
                      aria-label="Enter business name"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="ui button is-primary"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    Search
                  </Button>
                </Form>
              )}
            </Formik>
            {searchData && searchData.length > 0 ? (
              <div className="sixten wide table-wrapper">
                <FWOTable
                  id="abnSearch"
                  columns={columns}
                  data={searchData}
                  onRowClicked={rowClicked}
                />
              </div>
            ) : null}
            <p></p>
            <p></p>
            <div className="right">
              <Button
                type="button"
                className="ui button is-secondary"
                onClick={cancelABNChange}
              >
                Cancel
              </Button>
              &nbsp;
              <Button
                type="button"
                disabled={!submitEnable}
                className="ui button is-primary"
                onClick={submitAbnChange}
              >
                Submit
              </Button>
            </div>
          </div>
        </article>
      </div>
    </Protected>
  )
}

export default ChangeABN
